// src/app/practice/new/page.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useAuthContext } from "@/contexts/AuthContext";
import { useRecordContext } from "@/contexts/RecordContext";
import { useEventLogger } from "@/lib/useEventLogger";
import { useKatarutaSuggest } from "@/hooks/useKatarutaSuggest";
import KatarutaSuggest from "@/components/KatarutaSuggest";

const MAX_IMAGES = 3;
const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4MB（Vercel のリクエスト上限に合わせる）

export default function PracticeNewPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const sourceReportId = searchParams.get("source") || null;
  const isRespondMode = searchParams.get("mode") === "respond";
  const sourceRoomId = searchParams.get("roomId") || null;
  // インラインフォームからの引き継ぎ（画像を追加する → クリック時）
  const prefillTitle = searchParams.get("title") || "";
  const prefillBody  = searchParams.get("body")  || "";
  // 編集モード
  const editId = searchParams.get("edit") || null;
  const { clerkUser } = useAuthContext();
  const recordContext = useRecordContext();

  if (!recordContext) {
    return <div className="p-6">Context未提供</div>;
  }

  const { userProfile, fetchUserProfile } = recordContext;
  const hasDisplayName = !!userProfile?.displayName?.trim();

  const [title, setTitle] = useState(prefillTitle);
  const [body, setBody] = useState(prefillBody);
  const [tool, setTool] = useState("");
  // 編集モード：既存の画像URL（保持するもの）
  const [visibility, setVisibility] = useState<"private" | "room" | "submit" | "public">(
    sourceRoomId ? "room" : "private"
  );
  const [editRoomId, setEditRoomId] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false); // DB保存済みで submit 状態
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [editLoading, setEditLoading] = useState(!!editId);

  // 編集モード：既存レポートを読み込む
  useEffect(() => {
    if (!editId) return;
    setEditLoading(true);
    fetch(`/api/practice?id=${encodeURIComponent(editId)}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error) return;
        setTitle(data.title ?? "");
        setBody(data.body ?? "");
        setTool(data.tool ?? "");
        setExistingImageUrls(Array.isArray(data.imageUrls) ? data.imageUrls : []);
        if (data.roomId) setEditRoomId(data.roomId);
        if (data.visibility) {
          setVisibility(data.visibility);
          if (data.visibility === "submit") setIsSubmitted(true);
        }
      })
      .catch(() => {})
      .finally(() => setEditLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // textarea 自動伸長（スクロール位置を保持）
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const suggest = useKatarutaSuggest({
    textareaRef: bodyRef,
    getText: () => body,
    setText: setBody,
  });
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const scrollY = window.scrollY;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    window.scrollTo({ top: scrollY, behavior: "instant" });
  }, [body]);

  // ツールバー：選択範囲をマークダウン記法で囲む
  const wrapSelection = (prefix: string, suffix: string) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const selected = body.slice(start, end);
    const newBody = body.slice(0, start) + prefix + selected + suffix + body.slice(end);
    setBody(newBody);
    requestAnimationFrame(() => {
      el.focus();
      if (selected) {
        el.setSelectionRange(start + prefix.length, end + prefix.length);
      } else {
        const pos = start + prefix.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  // 画像
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { log: logEvent } = useEventLogger();
  const [loading, setLoading] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState("");
  const [titleError, setTitleError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const remaining = MAX_IMAGES - imageFiles.length;
    const candidates = files.slice(0, remaining);
    if (!candidates.length) return;

    const oversized = candidates.find((f) => f.size > MAX_FILE_BYTES);
    if (oversized) {
      alert(`「${oversized.name}」は大きすぎます（上限4MB）。\nサイズを小さくしてから再選択してください。`);
      e.target.value = "";
      return;
    }

    setImageFiles((prev) => [...prev, ...candidates]);
    candidates.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImagePreviews((prev) => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
    // 同じファイルを再選択できるようにリセット
    e.target.value = "";
  };

  const removeImage = (idx: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadImages = async (): Promise<string[]> => {
    if (!imageFiles.length) return [];
    const fd = new FormData();
    imageFiles.forEach((f) => fd.append("images", f));
    const res = await fetch("/api/practice-images", { method: "POST", body: fd });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.error ?? "画像のアップロードに失敗しました");
    }
    const data = await res.json();
    return data.urls as string[];
  };

  const submit = async (overrideName?: string) => {
    if (!title.trim()) {
      setTitleError("タイトルを入力してください");
      titleRef.current?.focus();
      titleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!body.trim()) {
      setBodyError("本文を入力してください");
      bodyRef.current?.focus();
      bodyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);

    try {
      // 1. 新しい画像をアップロード
      const newImageUrls = await uploadImages();
      // 既存URLと新規URLを合算（最大3枚）
      const combinedImageUrls = [...existingImageUrls, ...newImageUrls].slice(0, 3);

      if (editId) {
        // ── 編集モード：PATCH ──
        const res = await fetch("/api/practice", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: editId,
            title: title.trim(),
            body: body.trim(),
            tool: tool.trim() || null,
            imageUrls: combinedImageUrls,
            ...(editRoomId ? { visibility } : {}),
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          alert(data?.error ?? "更新に失敗しました");
          setLoading(false);
          return;
        }

        router.push("/practice");
        router.refresh();
      } else {
        // ── 新規作成：POST ──
        const res = await fetch("/api/practice", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: title.trim(),
            body: body.trim(),
            tool: tool.trim() || null,
            displayName: overrideName ?? userProfile?.displayName ?? tempDisplayName ?? "",
            sourceReportId: sourceReportId ?? null,
            roomId: sourceRoomId ?? null,
            visibility: sourceRoomId ? visibility : "private",
            imageUrls: combinedImageUrls,
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          alert(data?.error ?? "投稿に失敗しました");
          setLoading(false);
          return;
        }

        logEvent("practice_report_created");
        router.push("/practice");
        router.refresh();
      }
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "通信エラー");
    }

    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!hasDisplayName) {
      setShowNameModal(true);
      return;
    }
    await submit();
  };

  const handleSaveDisplayName = async () => {
    const name = tempDisplayName.trim();
    if (!name) return;

    await fetch("/api/user-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ displayName: name }),
    });

    await fetchUserProfile();
    setShowNameModal(false);
    setTempDisplayName("");
    await submit(name);
  };

  if (editLoading) {
    return (
      <main className="min-h-screen">
        <div className="mx-auto max-w-2xl px-4 py-8 text-sm text-gray-500">読み込み中...</div>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <div className="mx-auto max-w-2xl px-4 py-8">

        <div className="mb-6">
          <Link
            href="/practice"
            className="text-sm text-blue-600 underline hover:text-blue-800"
          >
            ← 実践レポートへ戻る
          </Link>
        </div>

        <h1 className="text-2xl font-bold mb-4">
          {editId ? "実践レポートを編集する" : "実践レポートを書く"}
        </h1>

        {!editId && (
          <p className="text-sm text-gray-600 mb-6">
            どんな場で、どのように使い、何が起きたかを簡単に教えてください。短くても大丈夫です。
          </p>
        )}

        {sourceReportId && !editId && (
          <div className="mb-5 text-xs text-gray-400 border border-gray-200 rounded px-3 py-2">
            実践レポートをきっかけに書いています
          </div>
        )}

        <div className="space-y-5">

          <div>
            <label className="text-sm font-medium">タイトル</label>
            <input
              ref={titleRef}
              value={title}
              onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(null); }}
              className={`w-full border border-gray-200 rounded px-3 py-2 mt-1 ${titleError ? "border-red-400" : ""}`}
              placeholder="例：自己紹介で使った / 会議のアイスブレイクで使った"
            />
            {titleError && (
              <p className="text-xs text-red-500 mt-1">{titleError}</p>
            )}
            <div className="flex justify-end mt-0.5">
              <span className={`text-xs ${title.length > 180 ? title.length >= 200 ? "text-red-500 font-medium" : "text-orange-400" : "text-gray-400"}`}>
                {title.length} / 200
              </span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">本文</label>
            {/* ツールバー */}
            <div className="flex gap-1 mt-1 mb-1 flex-wrap">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapSelection("**", "**"); }}
                className="px-2 py-0.5 text-sm font-bold border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="太字"
              >
                B
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapSelection("*", "*"); }}
                className="px-2 py-0.5 text-sm italic border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="斜体"
              >
                I
              </button>
              <span className="w-px bg-gray-200 mx-0.5" />
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapSelection("### ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="見出し H3"
              >
                H3
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapSelection("#### ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="見出し H4"
              >
                H4
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapSelection("- ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="箇条書き"
              >
                ・
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapSelection("1. ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="番号付きリスト"
              >
                1.
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapSelection("> ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="引用"
              >
                "
              </button>
            </div>
            <div className="mb-2">
              <KatarutaSuggest
                enabled={suggest.enabled}
                setEnabled={suggest.setEnabled}
                suggestMode={suggest.suggestMode}
                setSuggestMode={suggest.setSuggestMode}
                cardType={suggest.cardType}
                setCardType={suggest.setCardType}
                freqMode={suggest.freqMode}
                setFreqMode={suggest.setFreqMode}
                isPaidOrTrial={suggest.isPaidOrTrial}
              />
            </div>
            <div className="relative">
              <textarea
                ref={bodyRef}
                value={body}
                onChange={(e) => { setBody(e.target.value); if (bodyError) setBodyError(null); suggest.onTextChange(e.target.value); }}
                onKeyDown={suggest.onKeyDown}
                rows={6}
                className={`w-full border border-gray-200 rounded px-3 py-2 ${bodyError ? "border-red-400" : ""}`}
                style={{ resize: "none", overflow: "hidden", minHeight: "9rem" }}
                placeholder={"・どんな場で：\n・どう使ったか：\n・何が起きたか："}
              />
              {suggest.suggestion && suggest.coords && (
                <button
                  type="button"
                  onClick={suggest.adopt}
                  style={{ position: "absolute", top: suggest.coords.top, left: suggest.coords.left }}
                  className="text-sm px-3 py-1 rounded-full bg-white/90 border border-gray-200 text-gray-700 shadow-sm hover:bg-gray-50 transition-colors z-10"
                >
                  {suggest.suggestion.word}
                </button>
              )}
            </div>
            {bodyError && (
              <p className="text-xs text-red-500 mt-1">{bodyError}</p>
            )}
            <div className="flex items-center justify-end mt-0.5">
              <span className={`text-xs ${body.length > 18000 ? body.length >= 20000 ? "text-red-500 font-medium" : "text-orange-400" : "text-gray-400"}`}>
                {body.length.toLocaleString()} / 20,000
              </span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">
              使ったツール（任意・複数可）
            </label>
            <input
              value={tool}
              onChange={(e) => setTool(e.target.value)}
              className="w-full border rounded px-3 py-2 mt-1"
              placeholder="例：カタルタ（スタンダード） / タイムフロー / 文法リバーシ など"
            />
          </div>

          {/* 画像アップロード */}
          <div>
            <label className="text-sm font-medium">
              写真（任意・最大{MAX_IMAGES}枚）
            </label>
            <p className="text-xs text-gray-400 mt-0.5 mb-2">
              場の様子、使ったカードなど。JPEG・PNG・WebP・GIF、各4MBまで。
            </p>

            {/* 既存画像（編集モード） */}
            {existingImageUrls.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {existingImageUrls.map((url, idx) => (
                  <div key={`existing-${idx}`} className="relative w-24 h-24 rounded overflow-hidden border border-gray-200">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={url}
                      alt={`既存写真 ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setExistingImageUrls((prev) => prev.filter((_, i) => i !== idx))}
                      className="absolute top-0.5 right-0.5 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none hover:bg-black/70"
                      aria-label="削除"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 新規追加プレビュー */}
            {imagePreviews.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {imagePreviews.map((src, idx) => (
                  <div key={idx} className="relative w-24 h-24 rounded overflow-hidden border border-gray-200">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={src}
                      alt={`プレビュー ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-0.5 right-0.5 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none hover:bg-black/70"
                      aria-label="削除"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(existingImageUrls.length + imageFiles.length) < MAX_IMAGES && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  className="hidden"
                  onChange={handleImageChange}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm px-4 py-2 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  + 写真を追加
                </button>
              </>
            )}
          </div>

          {/* 公開範囲 */}
          {(sourceRoomId || editRoomId) && (
            <div>
              <p className="text-sm font-medium mb-2">公開範囲</p>
              <div className="flex flex-col gap-2">
                {[
                  { value: "private", label: "下書き", desc: "自分だけ見られる" },
                  { value: "room", label: "ルーム内公開", desc: "このルームの参加者が見られる" },
                  { value: "submit", label: "提出", desc: "ホストと自分だけが見られる（変更不可）" },
                  { value: "public", label: "全体公開", desc: "誰でも見られる" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-2 ${isSubmitted && opt.value !== "submit" ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={opt.value}
                      checked={visibility === opt.value}
                      onChange={() => {
                        if (isSubmitted) return; // DB保存済みの提出はロック
                        setVisibility(opt.value as typeof visibility);
                      }}
                      disabled={isSubmitted && opt.value !== "submit"}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="text-sm font-medium">{opt.label}</span>
                      <span className="text-xs text-gray-400 ml-2">{opt.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-5 py-2 rounded bg-black text-white disabled:opacity-50"
            >
              {loading ? "保存中..." : (editId ? "保存する" : "投稿する")}
            </button>
            <p className="text-xs text-gray-400 mt-2">
              {isSubmitted
                ? "提出済みのため変更できません"
                : visibility === "submit"
                ? "保存すると変更できなくなります"
                : visibility === "public"
                ? "すぐに公開されます"
                : "あとから公開範囲を変更できます"}
            </p>
          </div>

        </div>
      </div>

      {showNameModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-3">
              投稿するには表示名の登録が必要です
            </h2>
            <input
              value={tempDisplayName}
              onChange={(e) => setTempDisplayName(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-4"
              placeholder="表示名"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNameModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveDisplayName}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                保存して投稿
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
